import { $$store } from './symbols';


type ActionType<T> = {
	[key in keyof T]: string;
}

export type ActionResponse<T> = {
	type: string;
	payload: {
		value: T;
		status: string;
		time: number;
	};
}

const STATIC_ACTION = 'STATIC_ACTION';
const REQUESTED = 'REQUESTED';
const RECEIVED = 'RECEIVED';

const actionStatus = {
	[STATIC_ACTION]: STATIC_ACTION,
	[REQUESTED]: REQUESTED,
	[RECEIVED]: RECEIVED
};

function createActionCreatorPack<A, S>({ api }: { api: A }) {

	type BoxType = {
		api: typeof api;
		dispatch: any;
		getState: () => S;
	}

	function createTypes<T>(types: Array<string>, prefix = ''): ActionType<T> {
		const typesObj = types
			.reduce((acc, type) => (acc[type] = `[${prefix}]: ${type}`, acc), {} as ActionType<T>);

		return typesObj;
	}

	function createStaticAction<T>(type: string): (value?: any) => ActionResponse<T> {
		return (value: any = null) => {
			return {
				type,
				payload: {
					value,
					status: STATIC_ACTION,
					time: Date.now()
				}
			};
		}
	}

	function createAsyncHelperAction<T>(type: string): (status: string, value?: any) => ActionResponse<T> {
		return (status: string, value: any = null) => {
			return {
				type,
				payload: {
					value,
					status,
					time: Date.now()
				}
			};
		}
	}

	type CreateAsyncActionAddons = {
		cache?: {
			asyncSelector: {
				selectIsFetching: (state) => boolean;
				selectIsLoaded: (state) => boolean;
				selectDidInvalidate: (state) => boolean;
				selectItem: (state) => any;
			};
		};
	};

	function createAsyncAction<T>(type: string) {
		const asyncHelperAction = createAsyncHelperAction(type);
		const box = {
			api,
			dispatch: (action) => window[$$store].dispatch(action),
			getState: () => window[$$store].getState()
		};

		return (fn: (box: BoxType, ...args) => Promise<T>, addons?: CreateAsyncActionAddons): (...args) => Promise<T> | void => {
			return (...args: Array<any>): Promise<T> => {
				const isCacheEnabled = Boolean(addons && addons.cache && addons.cache.asyncSelector);

				if (isCacheEnabled) {
					const { asyncSelector } = addons.cache;
					const state = box.getState();
					const isFetching = asyncSelector.selectIsFetching(state);
					const isLoaded = asyncSelector.selectIsLoaded(state);
					const didInvalidate = asyncSelector.selectDidInvalidate(state);
					const needRequestCancel = !isFetching && !didInvalidate && isLoaded;

					if (needRequestCancel) {
						return Promise.resolve(asyncSelector.selectItem(state));
					}
				}

				box.dispatch(asyncHelperAction(REQUESTED));

				return fn(box, ...args).then(response => {
					box.dispatch(asyncHelperAction(RECEIVED, response));
					return response;
				}).catch(() => undefined);
			};
		};
	}

	return {
		actionStatus,
		createTypes,
		createStaticAction,
		createAsyncAction
	};
}

export { actionStatus };
export default createActionCreatorPack;
